import { domain } from 'modules/data';
import { useMemo } from 'react';
import bemFactory from 'react-bem-factory';

import { FieldEntryProps } from '../../../../requestForm/components/FieldEntry/FieldEntry';
import FieldsSection from '../../../components/FieldsSection';
import { messages } from './DearRequestCard.messages';

const qa = bemFactory.qa('dear-fields-section');

const getDearTaxCalculationLabel = (details: domain.DearRequestDetails) => {
    switch (details.taxCalculation) {
        case domain.DearTaxCalculation.TaxInclusive:
            return messages.fieldTaxCalculationValueYes;

        case domain.DearTaxCalculation.TaxExclusive:
            return messages.fieldTaxCalculationValueNo;

        default:
            return null;
    }
};

export const useDearRequestCardLineItems = (details: domain.DearRequestDetails) => {
    const lines = details.itemLines;

    return useMemo(() => (lines ? [...lines] : []), [lines]);
};

export const useDearRequestCardAdditionalChargeItems = (details: domain.DearRequestDetails) => {
    const lines = details.additionalChargeLines;

    return useMemo(() => (lines ? [...lines] : []), [lines]);
};

export const useDearRequestCardFieldsDefsSupplier = (details: domain.DearRequestDetails): FieldEntryProps[] => {
    return useMemo(
        () => [
            {
                qa: qa('supplier'),
                title: messages.fieldSupplier,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.supplier,
            },
            {
                qa: qa('contact'),
                title: messages.fieldContact,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.contact,
            },
            {
                qa: qa('phone'),
                title: messages.fieldPhone,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.phone,
            },
            {
                qa: qa('billingAddress'),
                title: messages.fieldBillingAddress,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.billingAddress?.trim(),
            },
        ],
        [details]
    );
};

export const useDearRequestCardFieldsDefsAccounting = (details: domain.DearRequestDetails): FieldEntryProps[] => {
    return useMemo(
        () => [
            {
                qa: qa('approach'),
                title: messages.fieldApproach,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.approach,
            },
            {
                qa: qa('terms'),
                title: messages.fieldTerms,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.terms,
            },
            {
                qa: qa('requiredBy'),
                title: messages.fieldRequiredByDate,
                valueType: FieldsSection.Field.ValueType.Date,
                value: details.requiredByDate,
            },
            {
                qa: qa('inventoryAccount'),
                title: messages.fieldInventoryAccount,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.inventoryAccount,
            },
            {
                qa: qa('taxCalculation'),
                title: messages.fieldTaxCalculation,
                valueType: FieldsSection.Field.ValueType.String,
                value: getDearTaxCalculationLabel(details),
            },
            {
                qa: qa('taxRule'),
                title: messages.fieldTaxRule,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.taxRule,
            },
        ],
        [details]
    );
};

export const useDearRequestCardFieldsDefsShipping = (details: domain.DearRequestDetails): FieldEntryProps[] => {
    return useMemo(
        () => [
            {
                qa: qa('orderDate'),
                title: messages.fieldOrderDate,
                valueType: FieldsSection.Field.ValueType.Date,
                value: details.orderDate,
            },
            {
                qa: qa('location'),
                title: messages.fieldLocation,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.location,
            },
            {
                qa: qa('shippingAddress'),
                title: messages.fieldShippingAddress,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.shippingAddress?.trim(),
            },
        ],
        [details]
    );
};

export const useDearRequestCardFieldsDefsNotes = (details: domain.DearRequestDetails): FieldEntryProps[] => {
    return useMemo(
        () => [
            {
                qa: qa('note'),
                title: messages.fieldNote,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.note,
            },
            {
                qa: qa('memo'),
                title: messages.fieldMemo,
                valueType: FieldsSection.Field.ValueType.String,
                value: details.memo,
            },
        ],
        [details]
    );
};
